import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText, Send, ArrowRight, X, Sun, Moon, PlayCircle, Linkedin, Twitter, LogIn, Target, Menu, Search, Briefcase, LayoutDashboard, ScanSearch, ChevronRight, Mail, Phone, ArrowUp, ArrowUpRight, Layers3,
} from "lucide-react";
import { AiIcon } from "@/components/ui/ai-icon";
import { Logo } from "./Logo";
import { DemoModal } from "./DemoModal";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "./AuthContext";
import { AccountSettings } from "./AccountSettings";
import { PaywallModal } from "./PaywallModal";
import { AuthModal } from "./AuthModal";
import { NavDropdown, NavDropdownItem } from "./landing/NavDropdown";
import { DemoVideoSection } from "./landing/DemoVideoSection";
import { PricingSection } from "./landing/PricingSection";
import { ServicesSection, LayoutsSection, FinalCtaSection, SectionHeading } from "./landing/LandingSections";
import { MobileActionBar } from "./shared/MobileActionBar";

// Landing page content adopted wholesale from career-canvas (the separate
// marketing-site project, per the 2026-09-18 decision) -- explicit user
// request to replace this page's body sections with career-canvas's actual
// layout/copy rather than restyling the prior "Lovable-style redesign" body
// in place. The <header> and <footer> below are UNCHANGED from before this
// pass (still this app's own, more capable nav/footer, not career-canvas's)
// -- only the content between them (hero through the final CTA) was swapped.
// Every CTA below points at a real in-app route/handler, never an external
// marketing-site URL the way career-canvas's own version does.

// Sample resume used purely for the animated showcase previews.
// Exported so the standalone /templates page (TemplatesPage.jsx) can reuse
// the exact same preview data as the landing page's own template sections,
// instead of duplicating it.
export const sampleResume = {
  personalInfo: {
    fullName: "Morgan Sterling",
    title: "Head of Product Design",
    email: "morgan@studio.com",
    phone: "+1 (555) 204-1188",
    location: "New York, NY",
    website: "linkedin.com/in/morgan",
    photoUrl: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=500&fit=crop&q=80"
  },
  summary: "Award-winning product leader with 10+ years crafting elegant, high-conversion digital experiences for Fortune 500 brands and fast-scaling startups.",
  experience: [
    { id: "e1", company: "Vortex Labs", role: "Head of Design", startDate: "2021", endDate: "Present", description: "Scaled the design org from 3 to 24 across 5 product lines.\nDrove a 38% lift in activation through a unified design system." },
    { id: "e2", company: "Northwind", role: "Senior Product Designer", startDate: "2017", endDate: "2021", description: "Led redesign of flagship app used by 4M+ monthly users.\nShipped 60+ experiments improving retention by 22%." }
  ],
  education: [
    { id: "ed1", school: "Rhode Island School of Design", degree: "BFA, Graphic Design", startDate: "2011", endDate: "2015", description: "" }
  ],
  skills: ["Product Strategy", "Design Systems", "Figma", "User Research", "Prototyping", "Leadership", "Roadmapping"],
  projects: [
    { id: "p1", name: "Atlas Design System", role: "Creator", link: "", description: "Open-source design system adopted by 12 internal teams." }
  ]
};

// Auto Application Bundle -- the three real entry flows (see
// NewApplicationPage.jsx/CareerQuestionnairePage.jsx/AiCareerBuilderPage.jsx),
// routed to directly so a visitor who already knows which one they want
// skips the intermediate picker. This is NOT a fourth application-
// generation system -- every route here is one that already exists and
// already converges into the same Application Bundle pipeline.
const APPLICATION_BUNDLE_PATHS = [
  {
    icon: FileText, title: "I Have a Resume", label: "Upload PDF/DOCX",
    copy: "Upload your existing resume and we'll take it from there.",
    route: "/applications/new", testid: "application-bundle-path-upload",
  },
  {
    // Career Profile (Phase 5 cutover, 2026-09-22) -- repoints this card
    // from the old one-shot CareerQuestionnairePage.jsx to the new
    // persistent, section-by-section Career Profile. That old route stays
    // mounted (App.js) but unlinked -- no bookmark/back-compat break, just
    // no longer the entry point a new visitor reaches.
    icon: ScanSearch, title: "I Don't Have a Resume", label: "Build Your Career Profile",
    copy: "Answer a few quick questions about your career and we'll build one from your real answers.",
    route: "/career-profile", testid: "application-bundle-path-career-profile",
  },
  {
    icon: AiIcon, title: "Build With AI", label: "AI Career Builder",
    copy: "Talk it through with AI and we'll draft your resume as you go.",
    route: "/applications/new/ai-builder", testid: "application-bundle-path-ai-builder",
  },
];

const HERO_PROMPTS = [
  "Ask ResumePilot360 anything about your career…",
  "Build a resume for a senior DevOps role…",
  "Which of my skills match this job posting?",
  "Make my resume fit on one page…",
  "Write a cover letter for my next application…",
];

// Smart Layout demo data: 5 distinct resume designs (different personas,
// column layouts, and accent colors) that the hero preview auto-rotates
// through, to show off Smart Layout adapting to different content.
// Smart Layout demo data: the SAME real resume content (Morgan Sterling,
// matching docs/Example-screenshot-resume.png), actually built through the
// product (POST /api/resumes) 8 times over -- once per named Smart Layout
// style -- then screenshotted from its own /r/:code public view
// (frontend/public/landing/resumes/*.png). Reusing one content set isolates
// the layout/style differences instead of conflating them with persona
// changes -- not hand-drawn CSS mockups, pixel-for-pixel what Smart Layout
// really renders.
const RESUME_VARIANTS = [
  { id: "executive", image: "/landing/resumes/executive.webp", label: "Executive", description: "Traditional, senior-level authority.", code: "GTDCBD" },
  { id: "minimal", image: "/landing/resumes/minimal.webp", label: "Minimal", description: "Distraction-free timeline layout.", code: "KR3EXS" },
  { id: "ats-clean", image: "/landing/resumes/ats-clean.webp", label: "ATS Clean", description: "Maximum ATS parsing compatibility.", code: "CQSS63" },
  { id: "executive-navy", image: "/landing/resumes/executive-navy.webp", label: "Executive Navy", description: "Navy sidebar with a photo, serif accents.", code: "NJI5FJ" },
  { id: "layout-05", image: "/landing/resumes/layout-05.webp", label: "Layout 05", description: "Solid black sidebar, expertise bars, boxed references.", code: "OIDNO6" },
  { id: "layout-08", image: "/landing/resumes/layout-08.webp", label: "Layout 08", description: "Navy sidebar with name/title inside it, dual skill-bar groups.", code: "TQ9CQB" },
  { id: "layout-17", image: "/landing/resumes/layout-17.webp", label: "Layout 17", description: "Navy sidebar, gold name banner, boxed education.", code: "B76TIW" },
  { id: "layout-30", image: "/landing/resumes/layout-30.webp", label: "Layout 30", description: "Navy banner over a dark dot-bar sidebar, pill headers.", code: "8K66BC" },
];

// Hero visual: three real Smart Layout renders of the same sample resume.
function HeroResumeStack() {
  const shots = [
    { src: "/landing/resumes/minimal.webp", cls: "left-0 top-10 w-[58%] -rotate-6 opacity-90" },
    { src: "/landing/resumes/layout-30.webp", cls: "right-0 top-4 w-[58%] rotate-6 opacity-90" },
    { src: "/landing/resumes/executive-navy.webp", cls: "left-1/2 top-0 w-[64%] -translate-x-1/2 z-10" },
  ];
  return (
    <div className="relative mx-auto hidden aspect-[5/4] w-full max-w-lg lg:col-span-5 lg:block" aria-hidden="true">
      <div className="absolute inset-x-8 bottom-0 top-16 -z-10 rounded-[2rem] bg-[radial-gradient(closest-side,rgba(166,124,82,0.28),transparent)] blur-2xl" />
      {shots.map((s) => (
        <img key={s.src} src={s.src} alt="" decoding="async" className={`absolute rounded-lg border border-black/5 bg-white shadow-[0_24px_60px_-24px_rgba(15,33,27,0.45)] ${s.cls}`} />
      ))}
    </div>
  );
}

// Career-canvas's HeroChatbox, ported: same cycling placeholder + pill
// composer, but submitting hands off into this app's real AI Career Copilot
// entry point (same initialAiMode/initialChatPrompt wiring the rest of this
// app already uses) instead of an external-site GET request.
// Enlarged (explicit user request, 2026-09-26, referencing docs/hero-
// update.png: "chatgpt bot can be elarged like the image i added") --
// wider max-width, more internal padding, a bigger send button -- same
// fields/behavior, purely a sizing change.
function HeroChatbox({ isLight, onStartBuilding, navigate }) {
  const [promptIndex, setPromptIndex] = useState(0);
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setPromptIndex((i) => (i + 1) % HERO_PROMPTS.length), 3200);
    return () => window.clearInterval(timer);
  }, []);

  const showHint = !focused && value.length === 0;

  const submit = (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) { onStartBuilding(); return; }
    navigate("/editor", { state: { startNew: true, initialAiMode: "chat", initialChatPrompt: trimmed } });
  };

  return (
    <div className="relative z-10 mx-auto mt-8 w-full max-w-3xl">
      <form
        onSubmit={submit}
        className={`flex items-center gap-3 rounded-full border p-3 pl-8 shadow-[0_20px_50px_-24px_rgba(166,124,82,0.35)] transition-shadow focus-within:shadow-[0_24px_60px_-24px_rgba(166,124,82,0.45)] ${
          isLight ? "border-[#DCEBE3] bg-white" : "border-white/10 bg-[#16332A]"
        }`}
      >
        <label htmlFor="hero-copilot" className="sr-only">Ask ResumePilot360</label>
        <span className="relative min-w-0 flex-1">
          <input
            id="hero-copilot"
            data-testid="hero-prompt-input"
            name="q"
            type="text"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className={`w-full bg-transparent py-1.5 text-base outline-none ${isLight ? "text-[#3D9970]" : "text-white"}`}
          />
          {showHint && (
            <span key={promptIndex} aria-hidden="true" className={`pointer-events-none absolute inset-y-0 left-0 flex w-full items-center truncate py-1.5 text-base ${isLight ? "text-[#6B8578]" : "text-[#7FA093]"}`}>
              {HERO_PROMPTS[promptIndex]}
            </span>
          )}
        </span>
        <button
          type="submit"
          data-testid="hero-prompt-submit"
          aria-label="Ask ResumePilot360"
          className="grid size-14 shrink-0 place-items-center rounded-full bg-[#A67C52] text-white shadow-[0_10px_28px_-12px_rgba(166,124,82,0.55)] transition-transform hover:scale-105 hover:bg-[#8A6640] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A67C52]"
        >
          <Send className="size-5" />
        </button>
      </form>
    </div>
  );
}

export const LandingPage = ({ onStartBuilding, onGoDashboard, theme = "dark", onToggleTheme }) => {
  const isLight = theme === "light";
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();

  // Auth modal (approved 2026-08-16): one shared modal instance for the
  // header's Log in/Get started buttons -- unchanged by this content pass.
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const authCopy = null;
  const openAuthModal = (mode) => { setAuthMode(mode); setShowAuth(true); };
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // The floating support bubble would sit on top of the open mobile menu.
  useEffect(() => {
    document.body.classList.toggle("rp-mobile-menu-open", showMobileMenu);
    return () => document.body.classList.remove("rp-mobile-menu-open");
  }, [showMobileMenu]);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On a fresh load with a URL hash (e.g. /#pricing-section from the footer
  // link, or a bookmarked/shared URL), the browser's native hash-scroll fires
  // before this component has rendered the target element, so it silently
  // does nothing. Retry once mounted.
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const revealTemplateGallery = () => {
    const el = document.getElementById("templates-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const focusHeroPrompt = () => {
    document.getElementById("hero-section")?.scrollIntoView({ behavior: "smooth" });
    document.querySelector('[data-testid="hero-prompt-input"]')?.focus();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <>
      {/* Header lives outside the overflow-x-hidden wrapper below -- that
          wrapper clips overflow on every axis, which silently breaks
          `position: sticky` on any descendant in every browser. */}
      <div className="sticky top-0 z-50 font-['DM_Sans']">
      <header
        className={`flex items-center justify-between gap-2 border-b px-4 py-3 backdrop-blur-xl transition-[background-color,box-shadow,border-color] duration-300 sm:px-6 ${
          isLight
            ? scrolled ? "border-black/[0.06] bg-white/80 shadow-[0_8px_30px_-12px_rgba(15,33,27,0.18)]" : "border-transparent bg-white/80"
            : scrolled ? "border-white/10 bg-[#0F211B]/80 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]" : "border-transparent bg-[#0F211B]/70"
        }`}
      >
        <a
          href="#hero-section"
          onClick={(e) => { e.preventDefault(); document.querySelector("#hero-section")?.scrollIntoView({ behavior: "smooth" }); }}
          className="flex shrink-0 sm:hidden"
          title="Back to top"
        >
          <Logo size="md" showWordmark={false} isLight={isLight} />
        </a>
        <a
          href="#hero-section"
          onClick={(e) => { e.preventDefault(); document.querySelector("#hero-section")?.scrollIntoView({ behavior: "smooth" }); }}
          className="hidden shrink-0 sm:flex"
          title="Back to top"
        >
          <Logo size="lg" isLight={isLight} />
        </a>
        <nav className={`hidden items-center gap-1 text-sm font-semibold lg:flex ${isLight ? "text-[#4F6B5E]" : "text-[#A9C2B6]"}`}>
          <NavDropdown
            label="Products"
            isLight={isLight}
            footer={({ close }) => (
              <button
                type="button"
                data-testid="nav-application-bundle-link"
                onClick={() => { close(); navigate("/applications/new"); }}
                className={`group flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors ${isLight ? "hover:bg-white" : "hover:bg-white/[0.05]"}`}
              >
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-[#3D9970] text-white"><Layers3 className="size-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-semibold ${isLight ? "text-[#1F3A2F]" : "text-white"}`}>Auto Application Bundle</span>
                  <span className={`block text-xs ${isLight ? "text-[#6B8578]" : "text-[#9FB8AC]"}`}>Paste a job, click once: resume, cover letter and ATS report</span>
                </span>
                <ArrowRight className="size-4 shrink-0 text-[#A67C52] transition-transform group-hover:translate-x-0.5" />
              </button>
            )}
          >
            {({ close }) => (
              <>
                {/* Journey order (2026-09-27): Build -> Improve / Optimize ->
                    Job Match -> Style. Improve Resume = resume health & ATS
                    readiness (job optional); Job Match = fit for one job,
                    with evidence -- complementary, not duplicates. */}
                <NavDropdownItem isLight={isLight} icon={FileText} label="Build Resume with AI" sub="Start from scratch or your career story" testid="nav-resume-builder-link"
                  onClick={() => { close(); navigate("/editor", { state: { startNew: true, initialAiMode: "chat" } }); }} />
                <NavDropdownItem isLight={isLight} icon={AiIcon} label="Improve / Optimize Resume" sub="Resume health & ATS readiness" testid="nav-ats-optimizer-link"
                  onClick={() => { close(); navigate("/ats-optimizer"); }} />
                <NavDropdownItem isLight={isLight} icon={Target} label="Job Match" sub="Job fit & evidence for a specific role" testid="nav-job-match-link"
                  onClick={() => { close(); navigate("/job-match-analyzer"); }} />
                <NavDropdownItem isLight={isLight} icon={Layers3} label="Smart Resume Style" sub="Make the final resume look right" testid="nav-templates-link"
                  onClick={() => { close(); navigate("/style-studio"); }} />
                <NavDropdownItem isLight={isLight} icon={Briefcase} label="Job Search" sub="Find roles that fit your resume" badge="Premium" testid="nav-job-search-link"
                  onClick={() => { close(); navigate("/jobs"); }} />
                <NavDropdownItem isLight={isLight} icon={ScanSearch} label="ATS Resume Checker" sub="Free instant check, no sign-up" badge="Free" testid="nav-ats-checker-link"
                  onClick={() => { close(); navigate("/ats-resume-checker"); }} />
              </>
            )}
          </NavDropdown>
          {[
            { label: "Features", testid: "nav-features-link", href: "#products-section" },
            { label: "Pricing", testid: "nav-pricing-link", href: "#pricing-section" },
            { label: "Compare", testid: "nav-compare-link", href: "/compare", route: "/compare" },
            { label: "About Us", testid: "nav-about-link", href: "/about", route: "/about" },
          ].map((l) => (
            <a
              key={l.testid}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => { e.preventDefault(); l.route ? navigate(l.route) : document.querySelector(l.href)?.scrollIntoView({ behavior: "smooth" }); }}
              className={`rounded-full px-3.5 py-2 transition-colors ${isLight ? "hover:bg-black/[0.04] hover:text-[#1F3A2F]" : "hover:bg-white/[0.06] hover:text-white"}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            data-testid="theme-toggle-button"
            onClick={onToggleTheme}
            title={isLight ? "Switch to dark UI" : "Switch to light UI"}
            aria-label={isLight ? "Switch to dark mode" : "Switch to light mode"}
            className={`hidden size-10 shrink-0 items-center justify-center rounded-full transition-colors sm:inline-flex ${isLight ? "text-[#4F6B5E] hover:bg-black/[0.05] hover:text-[#1F3A2F]" : "text-[#A9C2B6] hover:bg-white/[0.08] hover:text-white"}`}
          >
            {isLight ? <Moon className="size-[18px]" /> : <Sun className="size-[18px]" />}
          </button>
          {user ? (
            <>
              <button
                data-testid="header-account-chip"
                onClick={() => setShowAccountSettings(true)}
                title="Account settings"
                className={`flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-black ring-1 transition-colors ${isLight ? "bg-[#FFEBD7] text-[#A67C52] ring-[#A67C52]/20 hover:ring-[#A67C52]/50" : "bg-[#A67C52]/20 text-[#D4A574] ring-white/10 hover:ring-[#D4A574]/50"}`}
              >
                {(user.name || user.email || "U")[0].toUpperCase()}
              </button>
              <button
                data-testid="header-dashboard-btn"
                onClick={onGoDashboard}
                title="Go to Dashboard"
                className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-[#A67C52] px-4 text-sm font-semibold text-white shadow-[0_8px_20px_-10px_rgba(166,124,82,0.7)] transition-colors hover:bg-[#8A6640]"
              >
                <LayoutDashboard className="size-4" /> <span className="hidden sm:inline">Dashboard</span>
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="header-login-btn"
                onClick={() => openAuthModal("login")}
                className={`hidden h-10 shrink-0 items-center whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors sm:flex ${isLight ? "text-[#1F3A2F] hover:bg-black/[0.05]" : "text-white hover:bg-white/[0.08]"}`}
              >
                Log in
              </button>
              <button
                data-testid="header-get-started-btn"
                onClick={() => openAuthModal("signup")}
                className="group inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#A67C52] px-4 text-sm font-semibold text-white shadow-[0_8px_20px_-10px_rgba(166,124,82,0.7)] transition-colors hover:bg-[#8A6640] sm:px-5"
              >
                Get started <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </>
          )}
          <button
            data-testid="header-mobile-menu-button"
            onClick={() => setShowMobileMenu((v) => !v)}
            title="Menu"
            aria-label="Menu"
            aria-expanded={showMobileMenu}
            className={`flex size-10 shrink-0 items-center justify-center rounded-full transition-colors lg:hidden ${isLight ? "text-[#1F3A2F] hover:bg-black/[0.05]" : "text-white hover:bg-white/[0.08]"}`}
          >
            {showMobileMenu ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </header>

      {showMobileMenu && (
        <div
          data-testid="header-mobile-menu"
          className={`max-h-[calc(100vh-65px)] space-y-5 overflow-y-auto border-b px-4 pb-6 pt-3 backdrop-blur-xl animate-in fade-in-0 slide-in-from-top-2 duration-150 lg:hidden ${isLight ? "border-black/[0.06] bg-white/95" : "border-white/10 bg-[#0F211B]/95"}`}
        >
          <div>
            <p className={`px-2 pb-2 text-[11px] font-bold uppercase tracking-[0.16em] ${isLight ? "text-[#A67C52]" : "text-[#D4A574]"}`}>Products</p>
            <nav className="grid gap-1">
              {[
                { label: "Build Resume with AI", icon: FileText, testid: "mobile-nav-resume-builder", onClick: () => navigate("/editor", { state: { startNew: true, initialAiMode: "chat" } }) },
                { label: "Improve / Optimize Resume", icon: AiIcon, testid: "mobile-nav-ats-optimizer", onClick: () => navigate("/ats-optimizer") },
                { label: "Job Match", icon: Target, testid: "mobile-nav-job-match", onClick: () => navigate("/job-match-analyzer") },
                { label: "Smart Resume Style", icon: Layers3, testid: "mobile-nav-templates", onClick: () => navigate("/style-studio") },
                { label: "Job Search", icon: Briefcase, testid: "mobile-nav-job-search", onClick: () => navigate("/jobs") },
              ].map(({ label, icon: Icon, testid, onClick }) => (
                <button
                  key={testid}
                  type="button"
                  data-testid={testid}
                  onClick={() => { setShowMobileMenu(false); onClick(); }}
                  className={`flex items-center gap-3 rounded-xl px-2 py-2.5 text-left text-[15px] font-semibold transition-colors ${isLight ? "text-[#1F3A2F] hover:bg-[#F5F5F4]" : "text-white hover:bg-white/[0.05]"}`}
                >
                  <span className={`grid size-9 shrink-0 place-items-center rounded-lg ${isLight ? "bg-[#FFEBD7] text-[#A67C52]" : "bg-[#A67C52]/15 text-[#D4A574]"}`}><Icon className="size-4" /></span>
                  {label}
                </button>
              ))}
            </nav>
          </div>
          <div className={`grid grid-cols-2 gap-1 border-t pt-4 ${isLight ? "border-black/[0.06]" : "border-white/10"}`}>
            {[
              { label: "Features", testid: "mobile-nav-features", onClick: () => document.querySelector("#products-section")?.scrollIntoView({ behavior: "smooth" }) },
              { label: "Pricing", testid: "mobile-nav-pricing", onClick: () => document.querySelector("#pricing-section")?.scrollIntoView({ behavior: "smooth" }) },
              { label: "Compare", testid: "mobile-nav-compare", onClick: () => navigate("/compare") },
              { label: "About Us", testid: "mobile-nav-about", onClick: () => navigate("/about") },
            ].map((item) => (
              <button
                key={item.testid}
                type="button"
                data-testid={item.testid}
                onClick={() => { setShowMobileMenu(false); item.onClick(); }}
                className={`rounded-xl px-2 py-2.5 text-left text-sm font-semibold transition-colors ${isLight ? "text-[#4F6B5E] hover:bg-[#F5F5F4]" : "text-[#A9C2B6] hover:bg-white/[0.05]"}`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className={`space-y-2.5 border-t pt-4 ${isLight ? "border-black/[0.06]" : "border-white/10"}`}>
            <button
              data-testid="mobile-menu-start-building-button"
              onClick={() => { setShowMobileMenu(false); onStartBuilding(); }}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#A67C52] text-sm font-semibold text-white shadow-[0_10px_24px_-12px_rgba(166,124,82,0.7)] transition-colors hover:bg-[#8A6640]"
            >
              <AiIcon className="size-4" /> {t("nav.buildResume")}
            </button>
            <button
              data-testid="mobile-menu-watch-demo-button"
              onClick={() => { setShowMobileMenu(false); setShowDemo(true); }}
              className={`flex h-12 w-full items-center justify-center gap-2 rounded-full border text-sm font-semibold transition-colors ${isLight ? "border-black/10 text-[#1F3A2F] hover:bg-black/[0.03]" : "border-white/15 text-white hover:bg-white/[0.05]"}`}
            >
              <PlayCircle className="size-4" /> {t("nav.watchDemo")}
            </button>
            <div className="flex items-center gap-2 pt-1">
              {!user && (
                <button
                  data-testid="mobile-menu-login-button"
                  onClick={() => { setShowMobileMenu(false); openAuthModal("login"); }}
                  className={`flex h-10 flex-1 items-center justify-center gap-2 rounded-full text-sm font-semibold transition-colors sm:hidden ${isLight ? "text-[#1F3A2F] hover:bg-black/[0.04]" : "text-white hover:bg-white/[0.06]"}`}
                >
                  <LogIn className="size-4" /> Log in
                </button>
              )}
              <button
                data-testid="mobile-menu-theme-button"
                onClick={onToggleTheme}
                className={`flex h-10 flex-1 items-center justify-center gap-2 rounded-full text-sm font-semibold transition-colors ${isLight ? "text-[#1F3A2F] hover:bg-black/[0.04]" : "text-white hover:bg-white/[0.06]"}`}
              >
                {isLight ? <Moon className="size-4" /> : <Sun className="size-4" />} {isLight ? "Dark mode" : "Light mode"}
              </button>
              <LanguageSwitcher isLight={isLight} />
            </div>
          </div>
        </div>
      )}
      </div>

      <div className={`${isLight ? "bg-white text-[#3D9970]" : "bg-[#0F211B] text-white"} min-h-screen pb-[76px] md:pb-0 font-['DM_Sans'] overflow-x-hidden selection:bg-[#A67C52] selection:text-white transition-colors duration-300`}>
      <AccountSettings
        open={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        onUpgrade={() => { setShowAccountSettings(false); setShowPaywall(true); }}
      />

      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} isLight={isLight} reason="default" />

      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={() => {
          setShowAuth(false);
          if (!authCopy) onGoDashboard && onGoDashboard();
        }}
        title={authCopy?.title}
        subtitle={authCopy?.subtitle}
        initialMode={authMode}
      />

      {/* ===== Body content below is adopted from career-canvas's landing
          page (src/routes/index.tsx), section for section, with every CTA
          rewired to a real in-app route instead of an external marketing
          URL. ===== */}

      <section id="hero-section" className="relative pb-20 pt-12 md:pb-28 md:pt-16">
        <div
          className="absolute inset-x-0 top-0 -z-10 h-[54rem] pointer-events-none opacity-80"
          style={{
            background: isLight
              ? "radial-gradient(circle at 75% 18%, #D3F3DD, transparent 34%), radial-gradient(circle at 22% 10%, #FFEBD7, transparent 30%)"
              : "radial-gradient(circle at 75% 18%, #163824, transparent 34%), radial-gradient(circle at 22% 10%, #2F1E16, transparent 30%)",
          }}
        />
        {/* Two columns: text left, real resume renders right (hidden below lg). */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-x-20 gap-y-10 px-5 lg:grid-cols-12">
        <div className="text-center lg:col-span-7 lg:text-left">
          {/* text-5xl at lg: (was 6xl) -- the two-column split narrows this
              text column enough that 6xl would push "Build the resume that"
              back into a 2nd line despite whitespace-nowrap, the same
              overflow-vs-wrap risk noted below. */}
          <motion.h1 variants={itemVariants} className={`text-[1.8rem] font-['Manrope'] font-extrabold leading-[1.1] sm:text-5xl ${isLight ? "text-[#3D9970]" : "text-white"}`}>
            <span className="sm:whitespace-nowrap">Build the resume that</span><br />
            {/* Wording unchanged, per explicit user direction -- this pass is
                purely a visual/motion upgrade ("more dynamic and
                attractive"): a richer gradient fill + soft glow instead of
                flat accent color, plus a short underline sweep that draws in
                just after the headline's own fade/slide finishes (timed off
                containerVariants' staggerChildren + itemVariants' own
                duration above, not tied to framer's variant-inheritance,
                so it can't race or double-animate with the parent). */}
            <span className="relative inline-block bg-gradient-to-r from-[#A67C52] via-[#C9A876] to-[#A67C52] bg-clip-text text-transparent drop-shadow-[0_2px_14px_rgba(166,124,82,0.35)]">
              lands your next job.
              <motion.span
                aria-hidden="true"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: 0.85 }}
                style={{ originX: 0 }}
                className="absolute -bottom-1 left-0 h-[3px] w-full rounded-full bg-gradient-to-r from-[#A67C52] via-[#C9A876] to-[#A67C52]"
              />
            </span>
          </motion.h1>
          {/* Descriptive paragraph removed (explicit user request, 2026-09-26:
              "remove ... unnecessary information") -- per hero-update.png's
              own much leaner reference layout (headline -> one short line ->
              the input box directly, no long explainer paragraph). */}
          <motion.p variants={itemVariants} className={`mx-auto mt-6 max-w-xl text-base leading-7 lg:mx-0 ${isLight ? "text-[#6B8578]" : "text-[#9FB8AC]"}`}>
            AI resume builder, ATS optimizer, job match and cover letters — one workspace from first draft to sent application.
          </motion.p>
          <motion.div variants={itemVariants} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
            <button type="button" data-testid="hero-build-resume-btn" onClick={() => onStartBuilding()} className="inline-flex items-center gap-2 rounded-full bg-[#A67C52] px-6 py-3 text-sm font-bold text-white shadow-[0_10px_28px_-12px_rgba(166,124,82,0.55)] transition-colors hover:bg-[#8A6640]">
              Build My Resume <ArrowRight className="size-4" />
            </button>
            <button
              type="button"
              data-testid="hero-watch-demo-btn"
              onClick={() => setShowDemo(true)}
              className={`inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-bold transition-colors ${isLight ? "border-black/15 text-[#3D9970] hover:border-black/40" : "border-white/20 text-white hover:border-[#A67C52]"}`}
            >
              <PlayCircle className="size-4" /> Watch Demo
            </button>
          </motion.div>
          <motion.div variants={itemVariants}>
            <HeroChatbox isLight={isLight} onStartBuilding={onStartBuilding} navigate={navigate} />
          </motion.div>
        </div>
        <HeroResumeStack />
        </motion.div>
      </section>

      {/* Auto Application Bundle -- the flagship outcome-oriented pitch,
          positioned as the first section after the hero for maximum
          prominence. Primary CTA routes into the existing flow picker
          (/applications/new); each path card below routes directly into
          the matching existing entry page for a visitor who already knows
          which one they want. "Build My Resume" is kept as a secondary CTA
          for visitors not yet targeting a specific job (unchanged
          onStartBuilding handler, same one the hero already uses). */}
      <section id="application-bundle-section" className={`scroll-mt-20 border-y py-20 md:py-24 px-5 ${isLight ? "border-black/5 bg-[#FAFAF9]" : "border-white/10 bg-[#1C3B30]"}`}>
        <div className="mx-auto max-w-6xl">
          <SectionHeading
            isLight={isLight}
            eyebrow="Auto Application Bundle"
            title="One job. One complete application bundle."
          />

          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              data-testid="application-bundle-primary-cta"
              onClick={() => navigate("/applications/new")}
              className="inline-flex items-center gap-2 rounded-full bg-[#A67C52] px-7 py-3.5 text-sm font-bold text-white shadow-[0_10px_28px_-12px_rgba(166,124,82,0.55)] transition-colors hover:bg-[#8A6640]"
            >
              Create My Application Bundle <ArrowRight className="size-4" />
            </button>
            <button
              type="button"
              data-testid="application-bundle-secondary-cta"
              onClick={() => onStartBuilding()}
              className={`inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-bold transition-colors ${isLight ? "border-black/15 text-[#3D9970] hover:border-black/40" : "border-white/20 text-white hover:border-[#A67C52]"}`}
            >
              Build My Resume
            </button>
          </div>

          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {APPLICATION_BUNDLE_PATHS.map(({ icon: Icon, title, label, copy, route, testid }) => (
              <button
                key={title}
                type="button"
                data-testid={testid}
                onClick={() => navigate(route)}
                className={`group relative overflow-hidden rounded-xl border p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(166,124,82,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A67C52] focus-visible:ring-offset-2 ${isLight ? "border-[#DCEBE3] bg-white hover:border-[#A67C52]/40" : "border-white/10 bg-[#16332A] hover:border-[#A67C52]/50"}`}
              >
                <div className="relative flex items-center justify-between">
                  <span className={`grid size-10 place-items-center rounded-lg bg-[#FFEBD7] text-[#A67C52] transition-transform duration-300 group-hover:scale-110 ${isLight ? "" : "bg-[#A67C52]/15"}`}>
                    <Icon className="size-5" />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#A67C52]">{label}</span>
                </div>
                <h3 className="relative mt-5 text-lg font-bold">{title}</h3>
                <p className={`relative mt-2 text-sm leading-6 ${isLight ? "text-[#6B8578]" : "text-[#9FB8AC]"}`}>{copy}</p>
                <span className="relative mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#A67C52] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  Start here <ChevronRight className="size-3.5" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <ServicesSection isLight={isLight} navigate={navigate} />

      <LayoutsSection isLight={isLight} navigate={navigate} variants={RESUME_VARIANTS} />

      <DemoVideoSection isLight={isLight} onExpand={() => setShowDemo(true)} onStart={() => onStartBuilding()} />

      <PricingSection
        isLight={isLight}
        onStartFree={() => onStartBuilding()}
        onChoosePlan={() => (user ? setShowPaywall(true) : navigate("/signup"))}
      />

      <FinalCtaSection onStartBuilding={onStartBuilding} navigate={navigate} />

      <footer data-testid="site-footer" className={`relative overflow-hidden border-t ${isLight ? "border-black/10 bg-[#FBFAF7]" : "border-white/10 bg-[#0B1A15]"}`}>
        {/* Soft top glow so the footer reads as its own surface, not a page tail. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#A67C52]/50 to-transparent" />
        <div aria-hidden="true" className={`pointer-events-none absolute -top-40 left-1/2 h-80 w-[48rem] -translate-x-1/2 rounded-full blur-3xl ${isLight ? "bg-[#3D9970]/[0.07]" : "bg-[#3D9970]/[0.12]"}`} />

        <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-10">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="space-y-6 lg:col-span-4">
              <div className="space-y-3">
                <Logo size="sm" isLight={isLight} />
                <p className={`max-w-sm text-sm leading-relaxed ${isLight ? "text-[#4F6B5E]" : "text-[#9FB8AC]"}`}>
                  Resume Compiler Intelligence that understands your career and compiles the right evidence for every opportunity &mdash; one connected system, not six disconnected tools.
                </p>
              </div>

              <ul className="space-y-2 text-sm">
                <li>
                  <a data-testid="footer-email" href="mailto:support@resumepilot360.com" className={`group inline-flex items-center gap-3 break-all transition-colors ${isLight ? "text-[#1F3A2F] hover:text-[#A67C52]" : "text-[#DCE8E2] hover:text-[#D4A574]"}`}>
                    <span className={`flex size-8 shrink-0 items-center justify-center rounded-lg border ${isLight ? "border-black/10 bg-white" : "border-white/10 bg-white/5"}`}><Mail className="size-4" /></span>
                    support@resumepilot360.com
                  </a>
                </li>
                <li>
                  <a data-testid="footer-phone" href="tel:+19167209052" className={`group inline-flex items-center gap-3 transition-colors ${isLight ? "text-[#1F3A2F] hover:text-[#A67C52]" : "text-[#DCE8E2] hover:text-[#D4A574]"}`}>
                    <span className={`flex size-8 shrink-0 items-center justify-center rounded-lg border ${isLight ? "border-black/10 bg-white" : "border-white/10 bg-white/5"}`}><Phone className="size-4" /></span>
                    (916) 720-9052
                  </a>
                </li>
              </ul>

              <div className="flex items-center gap-2">
                {[
                  { testid: "footer-linkedin", href: "https://linkedin.com/company/sprytstack", label: "LinkedIn", Icon: Linkedin },
                  { testid: "footer-x", href: "https://x.com/", label: "X", Icon: Twitter },
                ].map(({ testid, href, label, Icon }) => (
                  <a key={testid} data-testid={testid} href={href} target="_blank" rel="noreferrer" aria-label={label}
                    className={`flex size-10 items-center justify-center rounded-full border transition-all hover:-translate-y-0.5 ${isLight ? "border-black/10 text-[#4F6B5E] hover:border-[#A67C52]/60 hover:text-[#A67C52]" : "border-white/10 text-[#9FB8AC] hover:border-[#D4A574]/60 hover:text-[#D4A574]"}`}>
                    <Icon className="size-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Columns mirror the header's Products menu (2026-09-28): same
                names, same journey order, same destinations. */}
            <nav aria-label="Footer" className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-8">
              {(() => {
                const linkCls = `group inline-flex items-center gap-1 py-1 text-left text-sm transition-colors ${isLight ? "text-[#4F6B5E] hover:text-[#0F211B]" : "text-[#9FB8AC] hover:text-white"}`;
                const col = (title, items) => (
                  <div>
                    <h4 className={`mb-4 text-sm font-semibold ${isLight ? "text-[#0F211B]" : "text-white"}`}>{title}</h4>
                    <ul className="space-y-1.5">
                      {items.map((it) => {
                        const inner = (
                          <>
                            {it.label}
                            <ArrowUpRight aria-hidden="true" className="size-3.5 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100 text-[#A67C52]" />
                          </>
                        );
                        return (
                          <li key={it.label}>
                            {it.href ? (
                              <a href={it.href} className={linkCls}>{inner}</a>
                            ) : (
                              <button type="button" data-testid={it.testid} onClick={it.onClick} className={linkCls}>{inner}</button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
                return (
                  <>
                    {col(t("footer.product"), [
                      { label: "Build Resume with AI", testid: "footer-link-editor", onClick: () => navigate("/editor", { state: { startNew: true, initialAiMode: "chat" } }) },
                      { label: "Improve / Optimize Resume", testid: "footer-link-improve", onClick: () => navigate("/ats-optimizer") },
                      { label: "LinkedIn to Resume", testid: "footer-link-linkedin-to-resume", onClick: () => navigate("/linkedin-to-resume") },
                      { label: "Job Match", testid: "footer-link-job-match", onClick: () => navigate("/job-match-analyzer") },
                      { label: "Smart Resume Style", testid: "footer-link-style-studio", onClick: () => navigate("/style-studio") },
                      { label: "Job Search", testid: "footer-link-jobs", onClick: () => navigate("/jobs") },
                    ])}
                    {col("Career Tools", [
                      { label: "Tailor Resume", testid: "footer-link-tailor", onClick: () => navigate("/resume-compiler") },
                      { label: "Cover Letters", testid: "footer-link-cover-letters", onClick: () => navigate("/cover-letter-generator") },
                      { label: "LinkedIn Optimizer", testid: "footer-link-linkedin-builder", onClick: () => navigate("/ai-studio/linkedin") },
                      { label: "Digital Portfolio", testid: "footer-link-digital-portfolio", onClick: () => navigate("/digital-portfolio") },
                      { label: "Application Tracker", testid: "footer-link-tracker", onClick: () => navigate("/tracker") },
                    ])}
                    {col(t("footer.resources"), [
                      { label: t("footer.templates"), testid: "footer-link-templates", onClick: revealTemplateGallery },
                      { label: t("footer.howItWorks"), href: "#application-bundle-section" },
                      { label: t("footer.watchDemo"), testid: "footer-link-demo", onClick: () => setShowDemo(true) },
                      { label: t("footer.pricing"), href: "#pricing-section" },
                      { label: t("footer.importChatgpt"), testid: "footer-link-paste", onClick: focusHeroPrompt },
                      { label: "ATS Resume Checker", testid: "footer-link-ats-checker", onClick: () => navigate("/ats-resume-checker") },
                      { label: t("footer.myResumes"), testid: "footer-link-dashboard", onClick: () => onGoDashboard && onGoDashboard() },
                    ])}
                  </>
                );
              })()}
            </nav>
          </div>

          <div className={`mt-16 flex flex-col-reverse gap-6 border-t pt-8 text-xs md:flex-row md:items-center md:justify-between ${isLight ? "border-black/10 text-[#6B8578]" : "border-white/10 text-[#7FA093]"}`}>
            <p>&copy; {new Date().getFullYear()} SprytStack LLC. {t("footer.rights")}</p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              {[
                { label: "Terms of Service", to: "/terms" },
                { label: t("footer.privacy"), to: "/privacy" },
                { label: "Refund Policy", to: "/refund" },
                { label: "Cookie Policy", to: "/cookies" },
                { label: t("footer.support"), to: "/contact" },
              ].map(({ label, to }) => (
                <button key={to} type="button" onClick={() => navigate(to)} className={`transition-colors ${isLight ? "hover:text-[#0F211B]" : "hover:text-white"}`}>{label}</button>
              ))}
              <button type="button" data-testid="footer-back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top"
                className={`ml-auto flex size-9 items-center justify-center rounded-full border transition-colors md:ml-2 ${isLight ? "border-black/10 hover:border-[#A67C52]/60 hover:text-[#A67C52]" : "border-white/10 hover:border-[#D4A574]/60 hover:text-[#D4A574]"}`}>
                <ArrowUp className="size-4" />
              </button>
            </div>
          </div>
        </div>

      </footer>

      <MobileActionBar isLight={isLight} />
      <DemoModal open={showDemo} onClose={() => setShowDemo(false)} onStart={() => onStartBuilding()} />
      </div>
    </>
  );
};
